"use client";

import * as React from "react";
import {
  editNumberDraft,
  formatNumberDraft,
  normalizeNumberDraft,
  numberDraftLocale,
  numberDraftRejectionMessage,
  numberDraftValue,
  settleNumberDraft,
  type NumberDraftInput,
  type NumberDraftOptions,
  type NumberDraftRejection,
} from "../../lib/number-draft";
import { omitUndefined, type Omittable } from "../../lib/props";
import { cn } from "../../lib/utils";
import { Box } from "./box";
import { Input, type InputProps } from "./input";
import { InputGroup, InputGroupInput } from "./input-group";
import { Text } from "./text";
import { Tooltip, TooltipPopup, TooltipTrigger } from "./tooltip";

export type AmountInputProps = Omit<
  InputProps,
  | "value"
  | "defaultValue"
  | "onChange"
  // Base UI's own string-valued `onValueChange` — this control's is numeric.
  | "onValueChange"
  | "type"
  | "inputMode"
  | "unstyled"
  | "nativeInput"
  | "backgroundColor"
  | "maxLength"
> & Omittable<{
  /** The settled number. `null` is empty. The text re-derives from it whenever
   *  it stops agreeing with what the draft reads as. */
  value: number | null;
  /** Fires after every accepted edit with what the draft now reads as —
   *  `null` while it is empty or only a sign. */
  onValueChange?: (value: number | null) => void;
  /** Fires on blur (and Enter, which blurs) with the settled value, after the
   *  text has been normalized to it (`322.` → `322`). */
  onValueCommit?: (value: number | null) => void;
  /** A fixed prefix ahead of the number (`$`, `€`, `kg`). Shown only while
   *  the draft has text — an empty field is a bare caret. The sign follows
   *  it: `$-1,234.56`. */
  symbol?: string;
  /** BCP-47 tag whose group and decimal glyphs the number is written in.
   *  Defaults to the runtime's locale. */
  locale?: string;
  /** Most decimals the value may carry. 0 forbids a decimal point. Default 2. */
  fractionDigits?: number;
  /** Most digits before the decimal point. Default 15. */
  maxIntegerDigits?: number;
  /** Whether a leading minus is accepted. Default true. */
  allowNegative?: boolean;
  /** Whether the integer digits group with the locale's separator as they
   *  are typed (`12,345`). Default true. Off writes the bare digits — an
   *  identifier or a year that a separator would misread as an amount. */
  grouping?: boolean;
  /** What a decimal past `fractionDigits` does: `"refuse"` (default) turns
   *  the key down; `"round"` accepts it while typing and rounds the value to
   *  `fractionDigits` on blur. */
  fractionOverflow?: NumberDraftOptions["fractionOverflow"];
  /** Edges of the rejected-edit state, carrying the message the tooltip
   *  shows, so a host can paint its own chrome (a cell ring, a row label). */
  onRejectionChange?: (message: string | null) => void;
  /** A commit-level rejection the host owns (a range check). Paints the same
   *  red ink as a rejected edit; the host supplies its own message. */
  invalid?: boolean;
}>;

/** The leading inset `Input` gives its text at each size (see `input.tsx`),
 *  worn by the symbol when it stands ahead of the input in the framed shell. */
const SYMBOL_INSET_CLASS: Record<
  Exclude<NonNullable<InputProps["size"]>, number>,
  string
> = {
  xs: "ps-[calc(--spacing(2)-1px)]",
  sm: "ps-[calc(--spacing(2.5)-1px)]",
  default: "ps-[calc(--spacing(3)-1px)]",
  lg: "ps-[calc(--spacing(3)-1px)]",
  xl: "ps-[calc(--spacing(3.5)-1px)]",
  "2xl": "ps-[calc(--spacing(4)-1px)]",
};

/** The caret, or the selection it spans. An input type with no selection API
 *  (`number`, `email`) answers null for both ends; that reads as a caret at
 *  the end of the text. */
function selectionOf(input: HTMLInputElement): [number, number] {
  const end = input.value.length;
  return [input.selectionStart ?? end, input.selectionEnd ?? end];
}

function toDraftInput(event: InputEvent): NumberDraftInput | null {
  const { inputType } = event;
  if (inputType.startsWith("insert")) {
    // Composition is not cancelable and belongs to the IME; it lands through
    // `onChange` and is normalized there.
    if (inputType === "insertCompositionText") return null;
    const text =
      event.data ?? event.dataTransfer?.getData("text/plain") ?? "";
    return { type: "insert", text };
  }
  if (inputType.startsWith("delete")) {
    return inputType.endsWith("Forward")
      ? { type: "delete-forward" }
      : { type: "delete-backward" };
  }
  return null;
}

/**
 * A number that formats as it is typed: digits group with the locale's
 * separator on every keystroke, the caret stays beside the digit it was next
 * to, and an edit the number cannot take — a third decimal, a letter, a
 * second minus — is refused in place and explained in a tooltip until the
 * next accepted edit or blur. Every edit is intercepted at `beforeinput`,
 * so a refused key never reaches the DOM and nothing has to be undone.
 *
 * The text is the DRAFT; `value` is the settled number. `onValueChange`
 * follows the draft, `onValueCommit` follows blur.
 */
export function AmountInput({
  value,
  onValueChange,
  onValueCommit,
  symbol,
  locale,
  fractionDigits = 2,
  maxIntegerDigits,
  allowNegative = true,
  grouping = true,
  fractionOverflow,
  onRejectionChange,
  invalid = false,
  bare = false,
  size,
  variant,
  className,
  ref,
  onBlur,
  onKeyDown,
  disabled,
  readOnly,
  ...props
}: AmountInputProps): React.ReactElement {
  const options = React.useMemo<NumberDraftOptions>(
    () => ({
      locale: numberDraftLocale(locale),
      maxFractionDigits: fractionDigits,
      maxIntegerDigits,
      allowNegative,
      grouping,
      fractionOverflow,
    }),
    [allowNegative, fractionDigits, fractionOverflow, grouping, locale, maxIntegerDigits],
  );
  const settle = React.useCallback(
    (settled: number | null) =>
      settled == null ? "" : formatNumberDraft(settled, options),
    [options],
  );

  const [text, setText] = React.useState(() => settle(value));
  const [rejected, setRejected] = React.useState<NumberDraftRejection | null>(
    null,
  );
  // The draft follows `value` only when the two disagree — echoing the
  // draft's own value back must not reformat `322.` mid-keystroke.
  const [syncedValue, setSyncedValue] = React.useState(value);
  if (value !== syncedValue) {
    setSyncedValue(value);
    if (numberDraftValue(text, options) !== value) setText(settle(value));
  }

  const inputRef = React.useRef<HTMLInputElement | null>(null);
  const setInputRef = React.useCallback(
    (node: HTMLInputElement | null) => {
      inputRef.current = node;
      if (typeof ref === "function") ref(node);
      else if (ref) ref.current = node;
    },
    [ref],
  );

  // The `beforeinput` listener is registered once; everything it reads goes
  // through refs so a re-render never re-binds it mid-gesture.
  const textRef = React.useRef(text);
  textRef.current = text;
  const optionsRef = React.useRef(options);
  optionsRef.current = options;
  const onValueChangeRef = React.useRef(onValueChange);
  onValueChangeRef.current = onValueChange;

  const applyEdit = React.useCallback(
    (
      input: HTMLInputElement,
      result: ReturnType<typeof editNumberDraft>,
    ) => {
      if (result.rejected) {
        setRejected(result.rejected);
        return;
      }
      setRejected(null);
      // Written to the DOM first so the caret lands with the text; React then
      // reconciles to the same string and leaves the selection alone.
      input.value = result.text;
      input.setSelectionRange(result.caret, result.caret);
      textRef.current = result.text;
      setText(result.text);
      onValueChangeRef.current?.(numberDraftValue(result.text, optionsRef.current));
    },
    [],
  );

  React.useEffect(() => {
    const input = inputRef.current;
    if (!input) return;
    const handleBeforeInput = (event: Event) => {
      if (!(event instanceof InputEvent) || input.readOnly || input.disabled) {
        return;
      }
      const draftInput = toDraftInput(event);
      if (!draftInput) return;
      event.preventDefault();
      applyEdit(
        input,
        editNumberDraft(
          textRef.current,
          selectionOf(input),
          draftInput,
          optionsRef.current,
        ),
      );
    };
    input.addEventListener("beforeinput", handleBeforeInput);
    return () => input.removeEventListener("beforeinput", handleBeforeInput);
  }, [applyEdit]);

  // Text that arrived without a cancelable `beforeinput` (autofill, an IME
  // commit) is normalized after the fact.
  const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const input = event.currentTarget;
    const [caret] = selectionOf(input);
    applyEdit(
      input,
      normalizeNumberDraft(input.value, caret, textRef.current, optionsRef.current),
    );
  };

  const handleBlur: NonNullable<InputProps["onBlur"]> = (event) => {
    const settledValue = settleNumberDraft(textRef.current, options);
    const settledText = settle(settledValue);
    if (settledText !== textRef.current) {
      textRef.current = settledText;
      setText(settledText);
    }
    setRejected(null);
    onValueCommit?.(settledValue);
    onBlur?.(event);
  };

  const handleKeyDown: NonNullable<InputProps["onKeyDown"]> = (event) => {
    onKeyDown?.(event);
    if (event.defaultPrevented) return;
    if (event.key === "Enter") event.currentTarget.blur();
  };

  const message =
    rejected === null ? null : numberDraftRejectionMessage(rejected, options);
  const onRejectionChangeRef = React.useRef(onRejectionChange);
  onRejectionChangeRef.current = onRejectionChange;
  React.useEffect(() => {
    onRejectionChangeRef.current?.(message);
  }, [message]);

  const showSymbol = symbol !== undefined && symbol !== "" && text !== "";
  const redInk = rejected !== null || invalid;
  const inputProps = {
    ...props,
    ref: setInputRef,
    value: text,
    onChange: handleChange,
    onBlur: handleBlur,
    onKeyDown: handleKeyDown,
    disabled,
    readOnly,
    inputMode: "decimal" as const,
    autoComplete: "off",
    "aria-invalid": redInk || undefined,
  };

  // The tooltip trigger IS the shell: its `render` element is the shell root
  // and the parts are its children, so the bubble anchors to the whole
  // control. Controlled `open`: the bubble appears the instant an edit is
  // refused and stays until the state clears, so the refusal is explained
  // where it happened rather than only on a hover the user may never make.
  const shellRoot = bare ? (
    <Box
      direction="row"
      align="center"
      fill="flex"
      cursor="text"
      // Bare = the host owns the metrics, the inset included (an editing
      // cell pads both its rest and edit states the same, so the value
      // never shifts). The shell carries no padding of its own — but it does
      // carry the rung's TEXT size, the same pair `Input`'s framed shell
      // wears (16px on touch, 14px on desktop; the xs rung a step down):
      // the bare input inherits its font, and left to the page's it grew
      // past the value it replaces.
      className={cn(
        "min-w-0",
        size === "xs" ? "text-sm sm:text-xs" : "text-base sm:text-sm",
        redInk && "text-destructive-foreground",
        className,
      )}
      onClick={() => inputRef.current?.focus()}
    />
  ) : (
    <InputGroup
      {...omitUndefined({ variant })}
      className={cn(redInk && "text-destructive-foreground", className)}
    />
  );

  // The symbol HUGS the first digit (`$3`, the way the value reads at rest),
  // so it is not an `InputGroupAddon` — that slot keeps an 8px gap for an
  // icon. In the framed shell the symbol takes the input's own leading inset
  // for its size and the input gives up its own.
  const symbolNode = showSymbol ? (
    <Text
      size={null}
      className={cn(
        "shrink-0 cursor-text",
        // A numeric `size` is the native character count, not a rung.
        !bare &&
          SYMBOL_INSET_CLASS[typeof size === "number" ? "default" : (size ?? "default")],
      )}
      onClick={() => inputRef.current?.focus()}
    >
      {symbol}
    </Text>
  ) : null;

  return (
    <Tooltip open={message !== null}>
      <TooltipTrigger render={shellRoot}>
        {symbolNode}
        {bare ? (
          <Input
            {...inputProps}
            bare
            className="min-w-0 flex-1 bg-transparent text-inherit placeholder:text-muted-foreground/72"
          />
        ) : (
          <InputGroupInput
            {...inputProps}
            size={size}
            className={showSymbol ? "[&_input]:ps-0" : undefined}
          />
        )}
      </TooltipTrigger>
      {message !== null ? (
        <TooltipPopup side="top" align="start">
          {message}
        </TooltipPopup>
      ) : null}
    </Tooltip>
  );
}
